import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Card, Table, Select, Space, Tag, Button, Tooltip, Switch, Typography, message } from "antd";
import { DesktopOutlined, MobileOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "../../../config/axiosInstance";
import dayjs from "dayjs";

const { Option } = Select;
const { Text } = Typography;

const monthNames = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
];

const DeviceUsageMonths = ({ client, year, darkTheme }) => {
    const [loading, setLoading] = useState(false);
    const [billingLoading, setBillingLoading] = useState(false);
    const [monthlyData, setMonthlyData] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [showBilled, setShowBilled] = useState(false);

    const fetchMonthlyUsage = useCallback(async () => {
        if (!client?._id || !year) return;

        try {
            setLoading(true);

            const params = {};
            if (showBilled) {
                params.billed = true;
            }

            const response = await axios.get(
                `/clients-report/${client._id}/device-usage-months/${year}`,
                { params }
            );

            const data = Array.isArray(response.data) ? response.data : [];
            setMonthlyData(data);

            if (data.length > 0) {
                const monthExists = data.some(item => item.month === selectedMonth);
                if (!selectedMonth || !monthExists) {
                    setSelectedMonth(data[0].month);
                }
            } else {
                setSelectedMonth(null);
            }
        } catch (error) {
            console.error("Error fetching monthly device usage:", error);
            message.error("Error loading monthly device usage");
        } finally {
            setLoading(false);
        }
    }, [client?._id, year, showBilled, selectedMonth]);

    useEffect(() => {
        fetchMonthlyUsage();
    }, [fetchMonthlyUsage]);

    const availableMonths = useMemo(() => {
        return monthlyData.map(item => ({
            value: item.month,
            label: monthNames.find(m => m.value === item.month)?.label || item.monthKey
        }));
    }, [monthlyData]);

    const selectedMonthData = useMemo(() => {
        return monthlyData.find(item => item.month === selectedMonth) || null;
    }, [monthlyData, selectedMonth]);

    const transformedData = useMemo(() => {
        if (!selectedMonthData?.events) return [];

        return selectedMonthData.events.map(event => ({
            key: event.eventID,
            eventID: event.eventID,
            event_mnemonic: event.event_mnemonic,
            event_name: event.event_name,
            event_beginDate: event.event_beginDate,
            event_endDate: event.event_endDate,
            event_totalDevices: event.devices?.length || 0,
            total_billable_days: event.total_billable_days || 0,
            devices: (event.devices || []).map(device => ({
                ...device,
                key: device.deviceID
            }))
        }));
    }, [selectedMonthData]);

    const getEventLogIds = (record) => {
        const ids = (record.devices || []).flatMap(device =>
            (device.configs || [])
                .map(config => config.logId)
                .filter(Boolean)
        );

        return [...new Set(ids)];
    };

    const handleMarkEventAsBilled = async (record, billedValue = true) => {
        const logIds = getEventLogIds(record);

        if (!logIds.length) {
            message.warning("No logs found for this event");
            return;
        }

        try {
            setBillingLoading(true);

            await axios.patch(`/clients-report/${client._id}/device-usage-months/mark-billed`, {
                logIds,
                billed: billedValue
            });

            message.success(billedValue ? "Event marked as billed" : "Event marked as pending");
            await fetchMonthlyUsage();
        } catch (error) {
            console.error("Error updating billing status:", error);
            message.error("Error updating billing status");
        } finally {
            setBillingLoading(false);
        }
    };

    const columns = [
        {
            title: "Mnemonic",
            dataIndex: "event_mnemonic",
            key: "event_mnemonic",
            render: (text, record) => (
                <Tooltip title={record.event_name}>
                    <span>{text}</span>
                </Tooltip>
            )
        },
        {
            title: "Begin Date",
            dataIndex: "event_beginDate",
            key: "event_beginDate",
            width: 120,
            render: (date) => (date ? dayjs(date).format("DD-MM-YYYY") : "-")
        },
        {
            title: "End Date",
            dataIndex: "event_endDate",
            key: "event_endDate",
            width: 120,
            render: (date) => (date ? dayjs(date).format("DD-MM-YYYY") : "-")
        },
        {
            title: "Total Devices",
            dataIndex: "event_totalDevices",
            key: "event_totalDevices",
            align: "center",
            render: (value) => (
                <strong style={{ color: darkTheme ? "limegreen" : "darkgreen", fontSize: "1.05rem" }}>
                    {value}
                </strong>
            )
        },
        {
            title: "Total Billable",
            dataIndex: "total_billable_days",
            key: "total_billable_days",
            align: "center",
            render: (value) => (
                <strong style={{ color: darkTheme ? "limegreen" : "darkgreen", fontSize: "1.05rem" }}>
                    {value}
                </strong>
            )
        },
        {
            title: "Actions",
            key: "actions",
            width: 220,
            align: "center",
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        loading={billingLoading}
                        onClick={() => handleMarkEventAsBilled(record, true)}
                    >
                        Mark billed
                    </Button>

                    <Button
                        size="small"
                        danger
                        loading={billingLoading}
                        onClick={() => handleMarkEventAsBilled(record, false)}
                    >
                        Undo
                    </Button>
                </Space>
            )
        }
    ];

    const expandedRowRender = (record) => {
        const flattenedData = record.devices.flatMap((device) => {
            return device.configs?.length > 0
                ? device.configs.map((config, configIndex) => ({
                    key: `${device.deviceID}-${configIndex}-${config.logId || "no-log"}`,
                    deviceID: device.deviceID,
                    serialNumber: device.serialNumber,
                    name: device.name,
                    type: device.type,
                    configName: config.name,
                    usedOn: config.usedOn,
                    billable: config.billable,
                    billed: config.billed,
                    billedAt: config.billedAt,
                    rowSpan: configIndex === 0 ? device.configs.length : 0
                }))
                : [{
                    key: `${device.deviceID}-no-config`,
                    deviceID: device.deviceID,
                    serialNumber: device.serialNumber,
                    name: device.name,
                    type: device.type,
                    configName: "-",
                    usedOn: "-",
                    billable: "-",
                    billed: false,
                    billedAt: null,
                    rowSpan: 1
                }];
        });

        const innerColumns = [
            {
                title: "Device Name",
                dataIndex: "name",
                key: "name",
                width: 200,
                render: (text, { rowSpan, serialNumber }) =>
                    rowSpan
                        ? {
                            children: (
                                <Tooltip title={`Serial Number: ${serialNumber}`}>
                                    <span>{text}</span>
                                </Tooltip>
                            ),
                            props: { rowSpan }
                        }
                        : { children: null, props: { rowSpan } }
            },
            {
                title: "Type",
                dataIndex: "type",
                key: "type",
                width: 100,
                align: "center",
                render: (type, { rowSpan }) => {
                    const icon = type === "mobile"
                        ? <MobileOutlined style={{ color: "#52c41a", fontSize: "18px" }} />
                        : <DesktopOutlined style={{ color: "#1677ff", fontSize: "18px" }} />;

                    return rowSpan
                        ? { children: icon, props: { rowSpan } }
                        : { children: null, props: { rowSpan } };
                }
            },
            {
                title: "Config Name",
                dataIndex: "configName",
                key: "configName",
                width: 220
            },
            {
                title: "Used On",
                dataIndex: "usedOn",
                key: "usedOn",
                width: 120,
                render: (date) => (date !== "-" ? dayjs(date).format("DD-MM-YYYY") : "-")
            },
            {
                title: "Billable",
                dataIndex: "billable",
                key: "billable",
                width: 130,
                render: (value) => {
                    const val = typeof value === "string" ? value.toLowerCase() : value;
                    if (val === "not billable" || val === 0) {
                        return <Tag color="red">NOT BILLABLE</Tag>;
                    }
                    return <Tag color="green">BILLABLE</Tag>;
                }
            },
            {
                title: "Billing Status",
                dataIndex: "billed",
                key: "billed",
                width: 140,
                render: (billed, row) => {
                    if (billed) {
                        return (
                            <Tooltip title={row.billedAt ? dayjs(row.billedAt).format("DD-MM-YYYY HH:mm") : ""}>
                                <Tag color="blue">BILLED</Tag>
                            </Tooltip>
                        );
                    }

                    return <Tag color="orange">PENDING</Tag>;
                }
            }
        ];

        return (
            <Table
                bordered
                columns={innerColumns}
                dataSource={flattenedData}
                pagination={false}
                rowKey="key"
                scroll={{ x: "max-content" }}
            />
        );
    };

    return (
        <Card
            title="Device Usage by Month"
            style={{ marginTop: 20 }}
            extra={
                <Space wrap>
                    <Select
                        value={selectedMonth}
                        onChange={setSelectedMonth}
                        style={{ width: 180 }}
                        placeholder="Select month"
                        allowClear
                    >
                        {availableMonths.map(month => (
                            <Option key={month.value} value={month.value}>
                                {month.label}
                            </Option>
                        ))}
                    </Select>

                    <Space size="small">
                        <Text>Show billed</Text>
                        <Switch checked={showBilled} onChange={setShowBilled} />
                    </Space>

                    <Button icon={<ReloadOutlined />} onClick={fetchMonthlyUsage}>
                        Refresh
                    </Button>
                </Space>
            }
        >
            <Table
                columns={columns}
                dataSource={transformedData}
                rowKey="key"
                loading={loading}
                expandable={{ expandedRowRender }}
                scroll={{ x: "max-content" }}
                bordered
                locale={{
                    emptyText: selectedMonth
                        ? "No device usage found for the selected month"
                        : "No monthly data available"
                }}
                summary={() => {
                    if (!selectedMonthData) return null;

                    return (
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={3}>
                                <strong>
                                    {monthNames.find(m => m.value === selectedMonthData.month)?.label || selectedMonthData.monthKey}
                                </strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="center">
                                <strong>
                                    {transformedData.reduce((sum, item) => sum + (item.event_totalDevices || 0), 0)}
                                </strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2} align="center">
                                <strong>{selectedMonthData.total_billable_days}</strong>
                            </Table.Summary.Cell>
                        </Table.Summary.Row>
                    );
                }}
            />
        </Card>
    );
};

export default DeviceUsageMonths;